import { format, type Locale } from "date-fns";
import { enUS, id as idLocale } from "date-fns/locale";
import type { LanguageCode } from "@/types/i18n";

export const throttle = (
	func: (...args: unknown[]) => void,
	limit: number,
): ((...args: unknown[]) => void) => {
	let lastFunc: ReturnType<typeof setTimeout> | null = null;
	let lastRan: number | null = null;

	return function (this: unknown, ...args: unknown[]) {
		if (lastRan === null) {
			func.apply(this, args);
			lastRan = Date.now();
		} else {
			if (lastFunc !== null) {
				clearTimeout(lastFunc);
			}
			lastFunc = setTimeout(
				() => {
					if (Date.now() - (lastRan as number) >= limit) {
						func.apply(this, args);
						lastRan = Date.now();
					}
				},
				limit - (Date.now() - (lastRan as number)),
			);
		}
	};
};

export function debounce<T extends (...args: unknown[]) => unknown>(
	func: T,
	wait: number,
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout | null = null;

	return (...args: Parameters<T>): void => {
		if (timeout) {
			clearTimeout(timeout);
		}

		timeout = setTimeout(() => {
			func(...args);
		}, wait);
	};
}

export function uid(): string {
	return (Date.now() + Math.floor(Math.random() * 1000)).toString();
}

export function getInitials(
	name: string | null | undefined,
	count?: number,
): string {
	if (!name || typeof name !== "string") {
		return "";
	}

	const initials = name
		.split(" ")
		.filter(Boolean)
		.map((part) => part[0].toUpperCase());

	return count && count > 0
		? initials.slice(0, count).join("")
		: initials.join("");
}

export function toAbsoluteUrl(pathname: string): string {
	const baseUrl = process.env.BASE_URL;

	if (baseUrl && baseUrl !== "/") {
		return process.env.BASE_URL + pathname;
	} else {
		return pathname;
	}
}

export function timeAgo(date: Date | string): string {
	const now = new Date();
	const inputDate = typeof date === "string" ? new Date(date) : date;
	const diff = Math.floor((now.getTime() - inputDate.getTime()) / 1000);

	if (diff < 60) return "just now";
	if (diff < 3600)
		return `${Math.floor(diff / 60)} minute${
			Math.floor(diff / 60) > 1 ? "s" : ""
		} ago`;
	if (diff < 86400)
		return `${Math.floor(diff / 3600)} hour${
			Math.floor(diff / 3600) > 1 ? "s" : ""
		} ago`;
	if (diff < 604800)
		return `${Math.floor(diff / 86400)} day${
			Math.floor(diff / 86400) > 1 ? "s" : ""
		} ago`;
	if (diff < 2592000)
		return `${Math.floor(diff / 604800)} week${
			Math.floor(diff / 604800) > 1 ? "s" : ""
		} ago`;
	if (diff < 31536000)
		return `${Math.floor(diff / 2592000)} month${
			Math.floor(diff / 2592000) > 1 ? "s" : ""
		} ago`;

	return `${Math.floor(diff / 31536000)} year${
		Math.floor(diff / 31536000) > 1 ? "s" : ""
	} ago`;
}

export function formatNumber(input: number, ISO?: string): string {
	return new Intl.NumberFormat(ISO ?? "id-ID").format(input);
}

export function formatCardNumber(cardNo: string) {
	return cardNo.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ");
}

export function formatSimplifiedAmount(amount: number, lang?: LanguageCode) {
	const locale = lang || "id";

	if (Number.isNaN(amount)) return "0";

	const suffixes: Record<LanguageCode, { value: number; suffix: string }[]> = {
		en: [
			{ value: 1_000_000_000_000, suffix: "T" }, // trillion
			{ value: 1_000_000_000, suffix: "B" }, // billion
			{ value: 1_000_000, suffix: "M" }, // million
			{ value: 1_000, suffix: "K" }, // thousand
		],
		id: [
			{ value: 1_000_000_000_000, suffix: "T" }, // triliun
			{ value: 1_000_000_000, suffix: "M" }, // miliar
			{ value: 1_000_000, suffix: "JT" }, // juta
			{ value: 1_000, suffix: "RB" }, // ribu
		],
	};

	for (const { value, suffix } of suffixes[locale]) {
		if (amount >= value) {
			const formatted = amount / value;

			const numberFormatter = new Intl.NumberFormat(
				locale === "id" ? "id-ID" : "en-US",
				{
					minimumFractionDigits: formatted % 1 === 0 ? 0 : 1,
					maximumFractionDigits: 1,
				},
			);

			return `${numberFormatter.format(formatted)}${suffix}`;
		}
	}

	return new Intl.NumberFormat(locale === "id" ? "id-ID" : "en-US").format(
		amount,
	);
}

export function formatDateLocalized(
	input: Date | string | number,
	options?: {
		lang?: LanguageCode;
		format?: string;
	},
): string {
	let date: Date;
	if (input instanceof Date) {
		date = input;
	} else if (typeof input === "string" || typeof input === "number") {
		date = new Date(input);
	} else {
		return "";
	}

	const locales: Record<LanguageCode, Locale> = {
		en: enUS,
		id: idLocale,
	};
	const locale = locales[options?.lang || "id"] || enUS;

	try {
		return format(date, options?.format || "PPP", { locale });
	} catch {
		return "";
	}
}

export function capitalizeFirstLetter(text: string) {
	if (text.length < 1) return text;
	return text.charAt(0).toUpperCase() + text.slice(1, text.length);
}
